import { useEffect, useRef, useState } from 'react';
import type { ReactElement } from 'react';
import type { ReactNode } from 'react';
import { getApiErrorMessage } from '@quoruum/frontend-common/api/errorMessage';

// ---- ChicletMultiSelect (internal) ----

function ChicletMultiSelect({
  options,
  value,
  onChange,
  disabled,
}: {
  options: ReadonlyArray<string>;
  value: string[];
  onChange: (next: string[]) => void;
  disabled: boolean;
}): ReactElement {
  const [search, setSearch] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const containerRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  const filtered = options.filter(
    (o) => !value.includes(o) && o.toLowerCase().includes(search.toLowerCase())
  );

  useEffect(() => {
    setActiveIndex(-1);
  }, [search, isOpen]);

  useEffect(() => {
    if (activeIndex >= 0 && listRef.current) {
      const item = listRef.current.children[activeIndex] as HTMLElement | undefined;
      item?.scrollIntoView({ block: 'nearest' });
    }
  }, [activeIndex]);

  useEffect(() => {
    function handleOutside(event: MouseEvent): void {
      if (!containerRef.current?.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleOutside);
    return () => document.removeEventListener('mousedown', handleOutside);
  }, []);

  function handleSelect(option: string): void {
    onChange([...value, option]);
    setSearch('');
  }

  function handleRemove(option: string): void {
    onChange(value.filter((v) => v !== option));
  }

  return (
    <div ref={containerRef} className="relative">
      <div className="flex min-h-[2rem] flex-wrap items-center gap-1 rounded-md border border-slate-300 bg-white px-2 py-1 text-xs shadow-sm transition focus-within:border-sky-500 focus-within:ring-2 focus-within:ring-sky-200">
        {value.map((item) => (
          <span
            key={item}
            className="inline-flex items-center gap-1 rounded bg-sky-100 px-1.5 py-0.5 text-xs font-medium text-sky-800"
          >
            {item}
            <button
              type="button"
              onClick={() => handleRemove(item)}
              disabled={disabled}
              className="text-sky-600 hover:text-sky-900 disabled:cursor-not-allowed"
              aria-label={`Remove ${item}`}
            >
              ×
            </button>
          </span>
        ))}
        <input
          type="text"
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setIsOpen(true);
          }}
          onFocus={() => setIsOpen(true)}
          onKeyDown={(e) => {
            if (e.key === 'ArrowDown') {
              e.preventDefault();
              setIsOpen(true);
              setActiveIndex((i) => (i < filtered.length - 1 ? i + 1 : i));
            } else if (e.key === 'ArrowUp') {
              e.preventDefault();
              setActiveIndex((i) => (i > 0 ? i - 1 : 0));
            } else if (e.key === 'Enter' && activeIndex >= 0 && filtered[activeIndex]) {
              e.preventDefault();
              handleSelect(filtered[activeIndex]);
            } else if (e.key === 'Backspace' && !search && value.length > 0) {
              onChange(value.slice(0, -1));
            } else if (e.key === 'Escape') {
              setIsOpen(false);
            }
          }}
          placeholder={value.length === 0 ? 'Type to search...' : ''}
          disabled={disabled}
          className="min-w-[8rem] flex-1 bg-transparent outline-none placeholder:text-slate-400"
        />
      </div>
      {isOpen && filtered.length > 0 ? (
        <div
          ref={listRef}
          className="absolute z-20 mt-1 max-h-48 w-full overflow-y-auto rounded-md border border-slate-200 bg-white shadow-lg"
        >
          {filtered.map((option, index) => (
            <button
              key={option}
              type="button"
              onMouseDown={(e) => {
                e.preventDefault();
                handleSelect(option);
              }}
              onMouseEnter={() => setActiveIndex(index)}
              className={`block w-full px-3 py-1.5 text-left text-xs text-slate-800 ${index === activeIndex ? 'bg-sky-50 text-sky-900' : 'hover:bg-sky-50 hover:text-sky-900'}`}
            >
              {option}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}

// ---- Public types ----

export type EntityDetailRow<TKey extends string = string> = {
  id?: string;
  key: string;
  value: ReactNode;
  editableFieldKey?: TKey;
  editableFieldValue?: string;
  editableFieldArrayValue?: string[];
  editableFieldBooleanValue?: boolean;
  editableFieldNumberValue?: number;
  editableFieldRowListValue?: RowListEntry[];
};

export type EditableTextFieldSpec = {
  editType: 'text';
  validateValue?: (value: string) => string | null;
};

export type EditableTextareaFieldSpec = {
  editType: 'textarea';
  validateValue?: (value: string) => string | null;
  heightPx?: number;
};

export type EditableMultiSelectFieldSpec = {
  editType: 'multiselect-enum';
  options: ReadonlyArray<string>;
};

export type EditableCheckboxFieldSpec = {
  editType: 'checkbox';
};

export type EditableNumberFieldSpec = {
  editType: 'number';
  validateValue?: (value: number) => string | null;
  parseAsInteger?: boolean;
  min?: number;
  max?: number;
  step?: number;
};

export type EditableSelectFieldSpec = {
  editType: 'select';
  options: ReadonlyArray<string>;
  allowEmptyOption?: boolean;
  emptyOptionLabel?: string;
};

// A row of freeform values keyed by column key, e.g. { queryParam: 'x', dateFormat: 'y' }.
export type RowListEntry = Record<string, string>;

export type EditableRowListTextColumn = {
  key: string;
  label: string;
  type: 'text';
  placeholder?: string;
};

export type EditableRowListSelectColumn = {
  key: string;
  label: string;
  type: 'select';
  options: ReadonlyArray<string>;
  allowEmptyOption?: boolean;
  emptyOptionLabel?: string;
};

export type EditableRowListColumn = EditableRowListTextColumn | EditableRowListSelectColumn;

export type EditableRowListFieldSpec = {
  editType: 'row-list';
  columns: ReadonlyArray<EditableRowListColumn>;
};

export type EditableFieldSpec =
  | EditableTextFieldSpec
  | EditableTextareaFieldSpec
  | EditableMultiSelectFieldSpec
  | EditableCheckboxFieldSpec
  | EditableNumberFieldSpec
  | EditableSelectFieldSpec
  | EditableRowListFieldSpec;

export type EditableFieldValue = string | string[] | boolean | number | RowListEntry[];

// ---- Component ----

type EditableEntityTableProps<TKey extends string = string> = {
  rows: ReadonlyArray<EntityDetailRow<TKey>>;
  fieldDefinitions: ReadonlyMap<TKey, EditableFieldSpec>;
  onSave: (fieldKey: TKey, value: EditableFieldValue) => Promise<void>;
  onClear?: (fieldKey: TKey) => void;
  clearableFieldKeys?: ReadonlySet<TKey>;
  header?: ReactNode;
  keyColumnWidth?: 'fixed' | 'content';
};

export function EditableEntityTable<TKey extends string = string>({
  rows,
  fieldDefinitions,
  onSave,
  onClear,
  clearableFieldKeys,
  header,
  keyColumnWidth = 'fixed',
}: EditableEntityTableProps<TKey>): ReactElement {
  const [editingFieldKey, setEditingFieldKey] = useState<TKey | null>(null);
  const [editingTextDraft, setEditingTextDraft] = useState('');
  const [editingArrayDraft, setEditingArrayDraft] = useState<string[]>([]);
  const [editingBooleanDraft, setEditingBooleanDraft] = useState(false);
  const [editingNumberDraft, setEditingNumberDraft] = useState('');
  const [editingRowListDraft, setEditingRowListDraft] = useState<RowListEntry[]>([]);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const textInputRef = useRef<HTMLInputElement>(null);
  const textAreaRef = useRef<HTMLTextAreaElement>(null);
  const numberInputRef = useRef<HTMLInputElement>(null);
  const selectInputRef = useRef<HTMLSelectElement>(null);

  useEffect(() => {
    if (editingFieldKey !== null) {
      const spec = fieldDefinitions.get(editingFieldKey);
      if (!spec || spec.editType === 'text') {
        textInputRef.current?.focus();
      } else if (spec.editType === 'textarea') {
        textAreaRef.current?.focus();
      } else if (spec.editType === 'number') {
        numberInputRef.current?.focus();
      } else if (spec.editType === 'select') {
        selectInputRef.current?.focus();
      }
    }
  }, [editingFieldKey, fieldDefinitions]);

  function handleStartEdit(fieldKey: TKey, row: EntityDetailRow<TKey>): void {
    setErrorMessage(null);
    setEditingFieldKey(fieldKey);
    const spec = fieldDefinitions.get(fieldKey);
    if (spec?.editType === 'multiselect-enum') {
      setEditingArrayDraft([...(row.editableFieldArrayValue ?? [])]);
      setEditingTextDraft('');
      setEditingBooleanDraft(false);
      setEditingNumberDraft('');
      setEditingRowListDraft([]);
    } else if (spec?.editType === 'checkbox') {
      setEditingBooleanDraft(row.editableFieldBooleanValue ?? false);
      setEditingTextDraft('');
      setEditingArrayDraft([]);
      setEditingNumberDraft('');
      setEditingRowListDraft([]);
    } else if (spec?.editType === 'number') {
      setEditingNumberDraft(
        row.editableFieldNumberValue !== undefined ? String(row.editableFieldNumberValue) : ''
      );
      setEditingTextDraft('');
      setEditingArrayDraft([]);
      setEditingBooleanDraft(false);
      setEditingRowListDraft([]);
    } else if (spec?.editType === 'select') {
      setEditingTextDraft(row.editableFieldValue ?? '');
      setEditingArrayDraft([]);
      setEditingBooleanDraft(false);
      setEditingNumberDraft('');
      setEditingRowListDraft([]);
    } else if (spec?.editType === 'row-list') {
      setEditingRowListDraft((row.editableFieldRowListValue ?? []).map((entry) => ({ ...entry })));
      setEditingTextDraft('');
      setEditingArrayDraft([]);
      setEditingBooleanDraft(false);
      setEditingNumberDraft('');
    } else {
      setEditingTextDraft(row.editableFieldValue ?? '');
      setEditingArrayDraft([]);
      setEditingBooleanDraft(false);
      setEditingNumberDraft('');
      setEditingRowListDraft([]);
    }
  }

  function handleCancel(): void {
    if (isSaving) return;
    setErrorMessage(null);
    setEditingFieldKey(null);
    setEditingTextDraft('');
    setEditingArrayDraft([]);
    setEditingBooleanDraft(false);
    setEditingNumberDraft('');
    setEditingRowListDraft([]);
  }

  async function handleSave(): Promise<void> {
    if (!editingFieldKey) return;
    const spec = fieldDefinitions.get(editingFieldKey);
    if (!spec) return;

    setErrorMessage(null);

    if (spec.editType === 'text' || spec.editType === 'textarea') {
      const validationError = spec.validateValue?.(editingTextDraft.trim()) ?? null;
      if (validationError) {
        setErrorMessage(validationError);
        return;
      }
    } else if (spec.editType === 'number') {
      const rawValue = editingNumberDraft.trim();
      if (rawValue === '') {
        setErrorMessage('Please enter a number.');
        return;
      }

      const parsedValue = Number(rawValue);
      if (!Number.isFinite(parsedValue)) {
        setErrorMessage('Please enter a valid number.');
        return;
      }

      if (spec.parseAsInteger && !Number.isInteger(parsedValue)) {
        setErrorMessage('Please enter a whole number.');
        return;
      }

      const validationError = spec.validateValue?.(parsedValue) ?? null;
      if (validationError) {
        setErrorMessage(validationError);
        return;
      }
    }

    let value: EditableFieldValue;
    if (spec.editType === 'multiselect-enum') {
      value = editingArrayDraft;
    } else if (spec.editType === 'checkbox') {
      value = editingBooleanDraft;
    } else if (spec.editType === 'number') {
      value = Number(editingNumberDraft.trim());
    } else if (spec.editType === 'row-list') {
      value = editingRowListDraft;
    } else {
      value = editingTextDraft.trim();
    }

    setIsSaving(true);
    try {
      await onSave(editingFieldKey, value);
      setEditingFieldKey(null);
      setEditingTextDraft('');
      setEditingArrayDraft([]);
      setEditingBooleanDraft(false);
      setEditingNumberDraft('');
      setEditingRowListDraft([]);
    } catch (error) {
      setErrorMessage(getApiErrorMessage(error, 'Unable to save field.'));
    } finally {
      setIsSaving(false);
    }
  }

  return (
    <>
      {header}
      <table
        className={`w-full border-collapse text-left text-sm ${
          keyColumnWidth === 'content' ? 'table-auto' : 'table-fixed'
        }`}
      >
        <tbody>
          {rows.map((row) => {
            const isEditing =
              row.editableFieldKey !== undefined && row.editableFieldKey === editingFieldKey;
            const spec =
              row.editableFieldKey !== undefined
                ? fieldDefinitions.get(row.editableFieldKey)
                : undefined;

            return (
              <tr key={row.id ?? row.key} className="border-b border-slate-100 last:border-b-0">
                <th
                  className={`bg-slate-50 px-3 py-2 text-xs font-medium text-slate-600 align-top ${
                    keyColumnWidth === 'content' ? 'w-auto whitespace-nowrap' : 'w-40 break-words'
                  }`}
                >
                  {row.key}
                </th>
                <td className="break-words px-3 py-2 text-xs text-slate-800 align-top">
                  {row.editableFieldKey !== undefined ? (
                    isEditing ? (
                      <div>
                        {spec?.editType === 'multiselect-enum' ? (
                          <div className="flex flex-col gap-2">
                            <ChicletMultiSelect
                              options={spec.options}
                              value={editingArrayDraft}
                              onChange={setEditingArrayDraft}
                              disabled={isSaving}
                            />
                            <div className="flex items-center gap-2">
                              <button
                                type="button"
                                onClick={() => void handleSave()}
                                className="rounded-md border border-slate-300 bg-white px-2 py-1 text-xs font-medium text-slate-700 shadow-sm transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
                                disabled={isSaving}
                              >
                                {isSaving ? 'Saving...' : 'Save'}
                              </button>
                              <button
                                type="button"
                                onClick={handleCancel}
                                className="rounded-md border border-slate-300 bg-white px-2 py-1 text-xs font-medium text-slate-700 shadow-sm transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
                                disabled={isSaving}
                              >
                                Cancel
                              </button>
                            </div>
                          </div>
                        ) : spec?.editType === 'checkbox' ? (
                          <div className="flex items-center gap-3">
                            <label className="inline-flex select-none items-center gap-2 text-xs text-slate-700">
                              <input
                                type="checkbox"
                                checked={editingBooleanDraft}
                                onChange={(e) => setEditingBooleanDraft(e.target.checked)}
                                className="h-4 w-4 rounded border-slate-300 text-sky-600 focus:ring-sky-500"
                                disabled={isSaving}
                                aria-label={`Edit ${row.key}`}
                              />
                              <span>{editingBooleanDraft ? 'True' : 'False'}</span>
                            </label>
                            <button
                              type="button"
                              onClick={() => void handleSave()}
                              className="rounded-md border border-slate-300 bg-white px-2 py-1 text-xs font-medium text-slate-700 shadow-sm transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
                              disabled={isSaving}
                            >
                              {isSaving ? 'Saving...' : 'Save'}
                            </button>
                            <button
                              type="button"
                              onClick={handleCancel}
                              className="rounded-md border border-slate-300 bg-white px-2 py-1 text-xs font-medium text-slate-700 shadow-sm transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
                              disabled={isSaving}
                            >
                              Cancel
                            </button>
                          </div>
                        ) : spec?.editType === 'number' ? (
                          <div className="flex items-center gap-2">
                            <input
                              ref={numberInputRef}
                              type="number"
                              value={editingNumberDraft}
                              onChange={(e) => setEditingNumberDraft(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                  e.preventDefault();
                                  void handleSave();
                                }
                                if (e.key === 'Escape') {
                                  e.preventDefault();
                                  handleCancel();
                                }
                              }}
                              min={spec.min}
                              max={spec.max}
                              step={spec.step}
                              className={`w-full rounded-md border bg-white px-2 py-1 text-xs text-slate-900 shadow-sm outline-none transition focus:ring-2 ${
                                errorMessage
                                  ? 'border-rose-500 focus:border-rose-500 focus:ring-rose-200'
                                  : 'border-slate-300 focus:border-sky-500 focus:ring-sky-200'
                              }`}
                              aria-label={`Edit ${row.key}`}
                              disabled={isSaving}
                            />
                            <button
                              type="button"
                              onClick={() => void handleSave()}
                              className="rounded-md border border-slate-300 bg-white px-2 py-1 text-xs font-medium text-slate-700 shadow-sm transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
                              disabled={isSaving}
                            >
                              {isSaving ? 'Saving...' : 'Save'}
                            </button>
                            <button
                              type="button"
                              onClick={handleCancel}
                              className="rounded-md border border-slate-300 bg-white px-2 py-1 text-xs font-medium text-slate-700 shadow-sm transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
                              disabled={isSaving}
                            >
                              Cancel
                            </button>
                          </div>
                        ) : spec?.editType === 'select' ? (
                          <div className="flex items-center gap-2">
                            <select
                              ref={selectInputRef}
                              value={editingTextDraft}
                              onChange={(e) => setEditingTextDraft(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                  e.preventDefault();
                                  void handleSave();
                                }
                                if (e.key === 'Escape') {
                                  e.preventDefault();
                                  handleCancel();
                                }
                              }}
                              className={`w-full rounded-md border bg-white px-2 py-1 text-xs text-slate-900 shadow-sm outline-none transition focus:ring-2 ${
                                errorMessage
                                  ? 'border-rose-500 focus:border-rose-500 focus:ring-rose-200'
                                  : 'border-slate-300 focus:border-sky-500 focus:ring-sky-200'
                              }`}
                              aria-label={`Edit ${row.key}`}
                              disabled={isSaving}
                            >
                              {spec.allowEmptyOption ? (
                                <option value="">{spec.emptyOptionLabel ?? 'None'}</option>
                              ) : null}
                              {spec.options.map((option) => (
                                <option key={option} value={option}>
                                  {option}
                                </option>
                              ))}
                            </select>
                            <button
                              type="button"
                              onClick={() => void handleSave()}
                              className="rounded-md border border-slate-300 bg-white px-2 py-1 text-xs font-medium text-slate-700 shadow-sm transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
                              disabled={isSaving}
                            >
                              {isSaving ? 'Saving...' : 'Save'}
                            </button>
                            <button
                              type="button"
                              onClick={handleCancel}
                              className="rounded-md border border-slate-300 bg-white px-2 py-1 text-xs font-medium text-slate-700 shadow-sm transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
                              disabled={isSaving}
                            >
                              Cancel
                            </button>
                          </div>
                        ) : spec?.editType === 'row-list' ? (
                          <div className="flex flex-col gap-2">
                            {editingRowListDraft.length > 0 ? (
                              <div className="flex gap-2">
                                {spec.columns.map((column) => (
                                  <span
                                    key={column.key}
                                    className="flex-1 text-xs font-medium text-slate-500"
                                  >
                                    {column.label}
                                  </span>
                                ))}
                                <span className="w-6" aria-hidden="true" />
                              </div>
                            ) : null}
                            <div className="flex flex-col gap-2">
                              {editingRowListDraft.map((entry, rowIndex) => (
                                <div key={rowIndex} className="flex items-center gap-2">
                                  {spec.columns.map((column) => (
                                    <div key={column.key} className="flex-1">
                                      {column.type === 'select' ? (
                                        <select
                                          value={entry[column.key] ?? ''}
                                          onChange={(e) => {
                                            const nextValue = e.target.value;
                                            setEditingRowListDraft((rows) =>
                                              rows.map((r, index) =>
                                                index === rowIndex
                                                  ? { ...r, [column.key]: nextValue }
                                                  : r
                                              )
                                            );
                                          }}
                                          disabled={isSaving}
                                          aria-label={column.label}
                                          className="w-full rounded-md border border-slate-300 bg-white px-2 py-1 text-xs text-slate-900 shadow-sm outline-none transition focus:border-sky-500 focus:ring-2 focus:ring-sky-200"
                                        >
                                          {column.allowEmptyOption ? (
                                            <option value="">
                                              {column.emptyOptionLabel ?? '-'}
                                            </option>
                                          ) : null}
                                          {column.options.map((option) => (
                                            <option key={option} value={option}>
                                              {option}
                                            </option>
                                          ))}
                                        </select>
                                      ) : (
                                        <input
                                          type="text"
                                          value={entry[column.key] ?? ''}
                                          onChange={(e) => {
                                            const nextValue = e.target.value;
                                            setEditingRowListDraft((rows) =>
                                              rows.map((r, index) =>
                                                index === rowIndex
                                                  ? { ...r, [column.key]: nextValue }
                                                  : r
                                              )
                                            );
                                          }}
                                          placeholder={column.placeholder}
                                          disabled={isSaving}
                                          aria-label={column.label}
                                          className="w-full rounded-md border border-slate-300 bg-white px-2 py-1 text-xs text-slate-900 shadow-sm outline-none transition focus:border-sky-500 focus:ring-2 focus:ring-sky-200"
                                        />
                                      )}
                                    </div>
                                  ))}
                                  <button
                                    type="button"
                                    onClick={() =>
                                      setEditingRowListDraft((rows) =>
                                        rows.filter((_, index) => index !== rowIndex)
                                      )
                                    }
                                    disabled={isSaving}
                                    aria-label="Remove row"
                                    className="rounded p-1 text-slate-500 hover:bg-slate-100 hover:text-rose-700 disabled:cursor-not-allowed disabled:opacity-60"
                                  >
                                    ✕
                                  </button>
                                </div>
                              ))}
                            </div>
                            <button
                              type="button"
                              onClick={() =>
                                setEditingRowListDraft((rows) => [
                                  ...rows,
                                  Object.fromEntries(
                                    spec.columns.map((column) => [column.key, ''])
                                  ),
                                ])
                              }
                              disabled={isSaving}
                              className="self-start rounded-md border border-slate-300 bg-white px-2 py-1 text-xs font-medium text-slate-700 shadow-sm transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
                            >
                              + Add row
                            </button>
                            <div className="flex items-center gap-2">
                              <button
                                type="button"
                                onClick={() => void handleSave()}
                                className="rounded-md border border-slate-300 bg-white px-2 py-1 text-xs font-medium text-slate-700 shadow-sm transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
                                disabled={isSaving}
                              >
                                {isSaving ? 'Saving...' : 'Save'}
                              </button>
                              <button
                                type="button"
                                onClick={handleCancel}
                                className="rounded-md border border-slate-300 bg-white px-2 py-1 text-xs font-medium text-slate-700 shadow-sm transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
                                disabled={isSaving}
                              >
                                Cancel
                              </button>
                            </div>
                          </div>
                        ) : spec?.editType === 'textarea' ? (
                          <div className="flex flex-col gap-2">
                            <textarea
                              ref={textAreaRef}
                              value={editingTextDraft}
                              onChange={(e) => setEditingTextDraft(e.target.value)}
                              onKeyDown={(e) => {
                                if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
                                  e.preventDefault();
                                  void handleSave();
                                }
                                if (e.key === 'Escape') {
                                  e.preventDefault();
                                  handleCancel();
                                }
                              }}
                              style={{ height: `${spec.heightPx ?? 120}px` }}
                              className={`w-full resize-y rounded-md border bg-white px-2 py-1 text-xs font-mono text-slate-900 shadow-sm outline-none transition focus:ring-2 ${
                                errorMessage
                                  ? 'border-rose-500 focus:border-rose-500 focus:ring-rose-200'
                                  : 'border-slate-300 focus:border-sky-500 focus:ring-sky-200'
                              }`}
                              aria-label={`Edit ${row.key}`}
                              disabled={isSaving}
                            />
                            <div className="flex items-center gap-2">
                              <button
                                type="button"
                                onClick={() => void handleSave()}
                                className="rounded-md border border-slate-300 bg-white px-2 py-1 text-xs font-medium text-slate-700 shadow-sm transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
                                disabled={isSaving}
                              >
                                {isSaving ? 'Saving...' : 'Save'}
                              </button>
                              <button
                                type="button"
                                onClick={handleCancel}
                                className="rounded-md border border-slate-300 bg-white px-2 py-1 text-xs font-medium text-slate-700 shadow-sm transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
                                disabled={isSaving}
                              >
                                Cancel
                              </button>
                            </div>
                          </div>
                        ) : (
                          <div className="flex items-center gap-2">
                            <input
                              ref={textInputRef}
                              type="text"
                              value={editingTextDraft}
                              onChange={(e) => setEditingTextDraft(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                  e.preventDefault();
                                  void handleSave();
                                }
                                if (e.key === 'Escape') {
                                  e.preventDefault();
                                  handleCancel();
                                }
                              }}
                              className={`w-full rounded-md border bg-white px-2 py-1 text-xs text-slate-900 shadow-sm outline-none transition focus:ring-2 ${
                                errorMessage
                                  ? 'border-rose-500 focus:border-rose-500 focus:ring-rose-200'
                                  : 'border-slate-300 focus:border-sky-500 focus:ring-sky-200'
                              }`}
                              aria-label={`Edit ${row.key}`}
                              disabled={isSaving}
                            />
                            <button
                              type="button"
                              onClick={() => void handleSave()}
                              className="rounded-md border border-slate-300 bg-white px-2 py-1 text-xs font-medium text-slate-700 shadow-sm transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
                              disabled={isSaving}
                            >
                              {isSaving ? 'Saving...' : 'Save'}
                            </button>
                            <button
                              type="button"
                              onClick={handleCancel}
                              className="rounded-md border border-slate-300 bg-white px-2 py-1 text-xs font-medium text-slate-700 shadow-sm transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
                              disabled={isSaving}
                            >
                              Cancel
                            </button>
                          </div>
                        )}
                        {errorMessage ? (
                          <p className="mt-1 text-xs text-rose-700">{errorMessage}</p>
                        ) : null}
                      </div>
                    ) : (
                      <div className="group">
                        <div className="flex items-center gap-2">
                          <div className="min-w-0 flex-1 break-all">{row.value}</div>
                          <button
                            type="button"
                            onClick={() => handleStartEdit(row.editableFieldKey!, row)}
                            className="rounded p-1 text-slate-500 opacity-0 transition hover:bg-slate-100 hover:text-slate-700 group-hover:opacity-100 focus:opacity-100 focus:outline-none focus:ring-2 focus:ring-sky-200"
                            aria-label={`Edit ${row.key}`}
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              viewBox="0 0 20 20"
                              fill="currentColor"
                              className="h-3.5 w-3.5"
                              aria-hidden="true"
                            >
                              <path d="M13.586 3.586a2 2 0 0 1 2.828 2.828l-9.193 9.193a1 1 0 0 1-.45.263l-3 1a1 1 0 0 1-1.264-1.264l1-3a1 1 0 0 1 .263-.45l9.193-9.193ZM12.172 5 5 12.172V14h1.828L14 6.828 12.172 5Z" />
                            </svg>
                          </button>
                        </div>
                        {onClear &&
                        row.editableFieldKey !== undefined &&
                        clearableFieldKeys?.has(row.editableFieldKey) ? (
                          <div className="mt-1">
                            <button
                              type="button"
                              onClick={() => onClear(row.editableFieldKey as TKey)}
                              className="rounded-md border border-slate-300 bg-white px-2 py-0.5 text-xs font-medium text-slate-600 transition hover:bg-slate-50 hover:text-rose-700 hover:border-rose-300"
                            >
                              Clear
                            </button>
                          </div>
                        ) : null}
                      </div>
                    )
                  ) : (
                    row.value
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </>
  );
}
